// 인증 상태 — Zustand persist (MEMBER / MANAGER / RATER 통합)
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import client, { clearTokens, setTokens, registerAuthFailureHandler, registerUserTypeGetter } from '@shared/api/httpClient';

export type UserType = 'MEMBER' | 'MANAGER' | 'RATER';

// Member 로그인 응답의 member 필드 구조와 동일하게 유지.
// Manager/Rater 로그인 시에는 managerId → memberId, name → nickName 으로 매핑해서 저장.
export interface MemberProfile {
  memberId: number;
  phone: string | null;
  nickName: string | null;
  email: string | null;
  userType: UserType;
  agencyId?: number | null;
}

interface AuthState {
  member: MemberProfile | null;
  hydrated: boolean;
  setAuth: (params: { accessToken: string; member: MemberProfile }) => void;
  patchMember: (patch: Partial<MemberProfile>) => void;
  logout: () => void;
  isAuthenticated: () => boolean;
}

export const useAuth = create<AuthState>()(
  persist(
    (set, get) => ({
      member: null,
      hydrated: false,
      setAuth: ({ accessToken, member }) => {
        setTokens(accessToken);
        set({ member });
      },
      patchMember: (patch) => {
        const cur = get().member;
        if (cur) set({ member: { ...cur, ...patch } });
      },
      logout: () => {
        clearTokens();
        set({ member: null });
      },
      isAuthenticated: () => Boolean(get().member),
    }),
    {
      name: 'navido.member.auth',
      partialize: (state) => ({ member: state.member }),
      onRehydrateStorage: () => (state) => {
        if (state) state.hydrated = true;
      },
    },
  ),
);

registerAuthFailureHandler(() => useAuth.getState().logout());
registerUserTypeGetter(() => useAuth.getState().member?.userType ?? null);

// React Query 캐시 정리 콜백 — main.tsx 에서 등록. logout / setAuth 시 이전 유저 캐시 완전 폐기.
let _queryCacheCleaner: (() => void) | null = null;
export function registerQueryCacheCleaner(fn: () => void) { _queryCacheCleaner = fn; }

const _origLogout = useAuth.getState().logout;
useAuth.setState({
  logout: () => { _queryCacheCleaner?.(); _origLogout(); },
});
const _origSetAuth = useAuth.getState().setAuth;
useAuth.setState({
  setAuth: (params) => {
    // 유저 전환 시 (다른 memberId 로 로그인) 이전 캐시 폐기.
    const prevId = useAuth.getState().member?.memberId;
    if (prevId != null && prevId !== params.member.memberId) _queryCacheCleaner?.();
    _origSetAuth(params);
  },
});

// 앱 부트 시 1회 — userType에 따라 맞는 refresh 엔드포인트 호출
export async function silentRefreshOnBoot(): Promise<void> {
  const { member, logout } = useAuth.getState();
  if (!member) return;

  const endpoint =
    member.userType === 'MEMBER'   ? '/account/refresh' :
    member.userType === 'RATER'    ? '/rater/auth/refresh' :
                                     '/admin/auth/refresh';

  try {
    const { data } = await client.post(endpoint);
    setTokens(data.accessToken);
  } catch {
    logout();
  }
}
